<template>
  <b-row>
    <b-col cols="12">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h3 class="mb-0">Banner Management</h3>
        <b-link to="/marketing/banners/new">
          <b-button variant="primary">
            <feather-icon icon="PlusIcon" />
            Create New Banner
          </b-button>
        </b-link>
      </div>

      <!-- Live Banners Section -->
      <b-card class="shadow-sm border-0">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">Live Banners</h5>
          <b-badge variant="success">{{ liveBanners.length }} Active</b-badge>
        </div>
        <div class="d-flex align-items-center justify-content-between">
          <small class="text-muted">
            <feather-icon icon="MoveIcon" class="mr-1" />
            Drag banners to reorder display priority
          </small>
          <b-button variant="primary" size="sm" @click="handleSaveOrder">
            <feather-icon icon="SaveIcon" />
            Save
          </b-button>
        </div>

        <div class="mt-2">
          <div v-if="loadingLive">
            <div
              v-for="i in 5"
              :key="i"
              class="d-flex justify-content-between align-items-center border rounded p-1 mb-2"
            >
              <div class="d-flex align-items-center" style="gap: 12px">
                <b-skeleton
                  type="circle"
                  width="20px"
                  height="20px"
                ></b-skeleton>
                <b-skeleton width="20px" height="20px"></b-skeleton>
                <b-skeleton-img
                  no-aspect
                  width="80px"
                  height="45px"
                  style="border-radius: 4px"
                />
                <div class="d-flex flex-column" style="gap: 4px">
                  <b-skeleton width="100px"></b-skeleton>
                  <b-skeleton width="180px"></b-skeleton>
                </div>
              </div>
              <div class="d-flex" style="gap: 8px">
                <b-skeleton width="30px" height="30px"></b-skeleton>
                <b-skeleton width="30px" height="30px"></b-skeleton>
                <b-skeleton width="30px" height="30px"></b-skeleton>
              </div>
            </div>
          </div>
          <draggable
            v-else
            :list="liveBanners"
            :group="{ name: 'liveBanners' }"
            :sort="true"
            class="d-flex flex-column"
            style="gap: 10px"
          >
            <div
              v-for="(banner, index) in liveBanners"
              :key="banner.bannerId"
              class="d-flex justify-content-between align-items-center border rounded p-1"
            >
              <div class="d-flex align-items-center" style="gap: 12px">
                <div
                  class="rounded-circle bg-primary text-white d-flex justify-content-center align-items-center"
                  style="width: 20px; height: 20px; font-size: 14px"
                >
                  {{ index + 1 }}
                </div>
                <feather-icon icon="GridIcon" class="text-muted" />

                <img
                  v-if="banner.resources[0].url"
                  :src="banner.resources[0].url"
                  alt="Banner"
                  style="width: 80px; height: 45px; object-fit: cover"
                />

                <div class="d-flex flex-column">
                  <h6 class="mb-0">{{ banner.name }}</h6>
                  <div
                    class="d-flex align-items-center justify-content-center"
                    style="gap: 12px"
                  >
                    <div class="text-muted small">
                      <feather-icon icon="CalendarIcon" />
                      {{
                        format(new Date(banner.startDate), "dd LLL yyyy hh:mm")
                      }}
                      -
                      {{
                        format(new Date(banner.endDate), "dd LLL yyyy hh:mm")
                      }}
                    </div>
                    <div>
                      <feather-icon icon="UsersIcon" class="mr-1" />
                      <span>{{ banner.segment }}</span>
                    </div>
                    <b-badge
                      :variant="banner.isLoginRequired ? 'success' : 'warning'"
                      >{{
                        banner.isLoginRequired ? " LOGIN" : "LOGOUT"
                      }}</b-badge
                    >
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex" style="gap: 8px">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="() => redirectToEdit(banner.bannerId)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <!-- <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="() => redirectToEdit(banner.bannerId)"
                >
                  <feather-icon icon="EyeIcon" />
                </b-button> -->
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="handleSelectBanner(banner, 'set draft')"
                >
                  <feather-icon icon="PauseIcon" />
                </b-button>
              </div>
            </div>
          </draggable>
        </div>
        <b-pagination
          v-if="paginationLive.total > 0"
          v-model="page.live"
          :total-rows="paginationLive.total"
          :per-page="10"
          first-number
          last-number
          class="float-right mt-1 mb-0"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </b-card>

      <b-card class="shadow-sm border-0">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">Scheduled Banners</h5>
          <b-badge variant="warning"
            >{{ scheduledBanners.length }} Scheduled</b-badge
          >
        </div>
        <div class="d-flex flex-column mt-2" style="gap: 10px">
          <div v-if="loadingScheduled">
            <div
              v-for="i in 5"
              :key="i"
              class="d-flex justify-content-between align-items-center border rounded p-1 mb-2"
            >
              <div class="d-flex align-items-center" style="gap: 12px">
                <b-skeleton-img
                  width="80px"
                  height="45px"
                  style="border-radius: 4px"
                />
                <div class="d-flex flex-column" style="gap: 4px">
                  <b-skeleton width="100px"></b-skeleton>
                  <b-skeleton width="180px"></b-skeleton>
                </div>
              </div>
              <div class="d-flex" style="gap: 8px">
                <b-skeleton width="30px" height="30px"></b-skeleton>
                <b-skeleton width="30px" height="30px"></b-skeleton>
                <b-skeleton width="30px" height="30px"></b-skeleton>
              </div>
            </div>
          </div>
          <div
            v-else
            v-for="banner in scheduledBanners"
            :key="banner.bannerId"
            class="d-flex justify-content-between align-items-center border rounded p-1"
          >
            <div class="d-flex align-items-center" style="gap: 12px">
              <img
                v-if="banner.resources[0].url"
                :src="banner.resources[0].url"
                alt="Banner"
                style="width: 80px; height: 45px; object-fit: cover"
              />

              <div class="d-flex flex-column">
                <h6 class="mb-0">{{ banner.name }}</h6>
                <div
                  class="d-flex align-items-center justify-content-center"
                  style="gap: 12px"
                >
                  <div class="text-muted small">
                    <feather-icon icon="CalendarIcon" />
                    {{
                      format(new Date(banner.startDate), "dd LLL yyyy hh:mm")
                    }}
                    -
                    {{ format(new Date(banner.endDate), "dd LLL yyyy hh:mm") }}
                  </div>
                  <div>
                    <feather-icon icon="UsersIcon" class="mr-1" />
                    <span>{{ banner.segment }}</span>
                  </div>
                  <b-badge
                    :variant="banner.isLoginRequired ? 'success' : 'warning'"
                    >{{ banner.isLoginRequired ? " LOGIN" : "LOGOUT" }}</b-badge
                  >
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex" style="gap: 8px">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="() => redirectToEdit(banner.bannerId)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <!-- <b-button
                variant="outline-secondary"
                size="sm"
                @click="() => redirectToEdit(banner.bannerId)"
              >
                <feather-icon icon="EyeIcon" />
              </b-button> -->
              <b-button variant="outline-success" size="sm">
                <feather-icon icon="PlayIcon" />
              </b-button>
            </div>
          </div>
        </div>
        <b-pagination
          v-if="paginationScheduled.total > 0"
          v-model="page.scheduled"
          :total-rows="paginationScheduled.total"
          :per-page="10"
          first-number
          last-number
          class="float-right mt-1 mb-0"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </b-card>

      <!-- DEACTIVATED BANNERS -->
      <b-card class="shadow-sm border-0">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">Deactivated Banners</h5>
          <b-badge>{{ deactivatedBanners.length }} Deactivated</b-badge>
        </div>
        <div class="d-flex flex-column mt-2" style="gap: 10px">
          <div v-if="loadingDeactivated">
            <div
              v-for="i in 5"
              :key="i"
              class="d-flex justify-content-between align-items-center border rounded p-1 mb-2"
            >
              <div class="d-flex align-items-center" style="gap: 12px">
                <b-skeleton-img
                  width="80px"
                  height="45px"
                  style="border-radius: 4px"
                />
                <div class="d-flex flex-column" style="gap: 4px">
                  <b-skeleton width="100px"></b-skeleton>
                  <b-skeleton width="180px"></b-skeleton>
                </div>
              </div>
              <div class="d-flex" style="gap: 8px">
                <b-skeleton width="30px" height="30px"></b-skeleton>
                <b-skeleton width="30px" height="30px"></b-skeleton>
                <b-skeleton width="30px" height="30px"></b-skeleton>
                <b-skeleton width="30px" height="30px"></b-skeleton>
              </div>
            </div>
          </div>
          <div
            v-else
            v-for="banner in deactivatedBanners"
            :key="banner.bannerId"
            class="d-flex justify-content-between align-items-center border rounded p-1"
          >
            <div class="d-flex align-items-center" style="gap: 12px">
              <img
                v-if="banner.resources[0].url"
                :src="banner.resources[0].url"
                alt="Banner"
                style="width: 80px; height: 45px; object-fit: cover"
              />

              <div class="d-flex flex-column">
                <h6 class="mb-0">{{ banner.name }}</h6>
                <div
                  class="d-flex align-items-center justify-content-center"
                  style="gap: 12px"
                >
                  <div class="text-muted small">
                    <feather-icon icon="CalendarIcon" />
                    {{
                      format(new Date(banner.startDate), "dd LLL yyyy hh:mm")
                    }}
                    -
                    {{ format(new Date(banner.endDate), "dd LLL yyyy hh:mm") }}
                  </div>
                  <div>
                    <feather-icon icon="UsersIcon" class="mr-1" />
                    <span>{{ banner.segment }}</span>
                  </div>
                  <b-badge
                    :variant="banner.isLoginRequired ? 'success' : 'warning'"
                    >{{ banner.isLoginRequired ? " LOGIN" : "LOGOUT" }}</b-badge
                  >
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex" style="gap: 8px">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="() => redirectToEdit(banner.bannerId)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <!-- <b-button
                variant="outline-secondary"
                size="sm"
                @click="() => redirectToEdit(banner.bannerId)"
              >
                <feather-icon icon="EyeIcon" />
              </b-button>
              <b-button
                variant="outline-success"
                size="sm"
                @click="handleSelectBanner(banner, 'active')"
              >
                <feather-icon icon="PlayIcon" />
              </b-button> -->
              <b-button
                variant="outline-danger"
                size="sm"
                @click="handleSelectBanner(banner, 'delete')"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
          </div>
        </div>

        <b-pagination
          v-if="paginationDeactivated.total > 0"
          v-model="page.deactivated"
          :total-rows="paginationDeactivated.total"
          :per-page="10"
          first-number
          last-number
          class="float-right mt-1 mb-0"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </b-card>

      <!-- DRAFT BANNERS -->
      <b-card class="shadow-sm border-0">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">Draft Banners</h5>
          <b-badge>{{ draftBanners.length }} Draft</b-badge>
        </div>
        <div class="d-flex flex-column mt-2" style="gap: 10px">
          <div v-if="loadingDraft">
            <div
              v-for="i in 5"
              :key="i"
              class="d-flex justify-content-between align-items-center border rounded p-1 mb-2"
            >
              <div class="d-flex align-items-center" style="gap: 12px">
                <b-skeleton-img
                  width="80px"
                  height="45px"
                  style="border-radius: 4px"
                />
                <div class="d-flex flex-column" style="gap: 4px">
                  <b-skeleton width="100px"></b-skeleton>
                  <b-skeleton width="180px"></b-skeleton>
                </div>
              </div>
              <div class="d-flex" style="gap: 8px">
                <b-skeleton width="30px" height="30px"></b-skeleton>
                <b-skeleton width="30px" height="30px"></b-skeleton>
                <b-skeleton width="30px" height="30px"></b-skeleton>
                <b-skeleton width="30px" height="30px"></b-skeleton>
              </div>
            </div>
          </div>
          <div
            v-else
            v-for="banner in draftBanners"
            :key="banner.bannerId"
            class="d-flex justify-content-between align-items-center border rounded p-1"
          >
            <div class="d-flex align-items-center" style="gap: 12px">
              <img
                v-if="banner.resources[0].url"
                :src="banner.resources[0].url"
                alt="Banner"
                style="width: 80px; height: 45px; object-fit: cover"
              />

              <div class="d-flex flex-column">
                <h6 class="mb-0">{{ banner.name }}</h6>
                <div
                  class="d-flex align-items-center justify-content-center"
                  style="gap: 12px"
                >
                  <div class="text-muted small">
                    <feather-icon icon="CalendarIcon" />
                    {{
                      format(new Date(banner.startDate), "dd LLL yyyy hh:mm")
                    }}
                    -
                    {{ format(new Date(banner.endDate), "dd LLL yyyy hh:mm") }}
                  </div>
                  <div>
                    <feather-icon icon="UsersIcon" class="mr-1" />
                    <span>{{ banner.segment }}</span>
                  </div>
                  <b-badge
                    :variant="banner.isLoginRequired ? 'success' : 'warning'"
                    >{{ banner.isLoginRequired ? " LOGIN" : "LOGOUT" }}</b-badge
                  >
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex" style="gap: 8px">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="() => redirectToEdit(banner.bannerId)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <!-- <b-button
                variant="outline-secondary"
                size="sm"
                @click="() => redirectToEdit(banner.bannerId)"
              >
                <feather-icon icon="EyeIcon" />
              </b-button> -->
              <b-button
                variant="outline-success"
                size="sm"
                @click="handleSelectBanner(banner, 'active')"
              >
                <feather-icon icon="PlayIcon" />
              </b-button>
              <!-- <b-button
                variant="outline-danger"
                size="sm"
                @click="handleSelectBanner(banner, 'delete')"
              >
                <feather-icon icon="TrashIcon" />
              </b-button> -->
            </div>
          </div>
        </div>

        <b-pagination
          v-if="paginationDraft.total > 0"
          v-model="page.draft"
          :total-rows="paginationDraft.total"
          :per-page="10"
          first-number
          last-number
          class="float-right mt-1 mb-0"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </b-card>
    </b-col>

    <b-modal
      id="banner-modal"
      header-text-variant="white"
      centered
      :title="mode === 'active' ? 'Active Banner' : 'Delete Banner'"
      @ok="handleChangeStatusBanner"
    >
      <p v-if="selectedBanner" class="text-black mb-0">
        Are you want to {{ mode }} the banner
        <span class="font-weight-bolder">"{{ selectedBanner.name }}"</span> ?
      </p>
    </b-modal>
  </b-row>
</template>

<script>
import {
  BCard,
  BCol,
  BRow,
  BButton,
  BBadge,
  BLink,
  BPagination,
  BSkeleton,
  BSkeletonImg,
  BModal,
} from "bootstrap-vue";
import draggable from "vuedraggable";
import { mapState, mapActions } from "vuex";
import { format } from "date-fns";
import useToastHandler from "@/services/toastHandler";

export default {
  components: {
    BCol,
    BRow,
    BCard,
    BButton,
    BBadge,
    BLink,
    draggable,
    BPagination,
    BSkeleton,
    BSkeletonImg,
    BModal,
  },
  data() {
    return {
      page: {
        live: 1,
        scheduled: 1,
        deactivated: 1,
        draft: 1,
      },
      selectedBanner: null,
      mode: "delete",
    };
  },
  computed: {
    ...mapState("marketing/banner", [
      "loading",
      "loadingLive",
      "loadingScheduled",
      "loadingDeactivated",
      "liveBanners",
      "scheduledBanners",
      "deactivatedBanners",
      "paginationLive",
      "paginationScheduled",
      "paginationDeactivated",
      "draftBanners",
      "paginationDraft",
      "loadingDraft",
    ]),
  },
  async created() {
    await Promise.all([
      this.getLiveBanners(),
      this.getScheduledBanners(),
      this.getDeactivatedBanners(),
      this.getDraftBanners(),
    ]);
  },
  watch: {
    "page.live"(newPage) {
      this.getLiveBanners({ page: newPage });
    },
    "page.scheduled"(newPage) {
      this.getScheduledBanners({ page: newPage });
    },
    "page.deactivated"(newPage) {
      this.getDeactivatedBanners({ page: newPage });
    },
    "page.draft"(newPage) {
      this.getDraftBanners({ page: newPage });
    },
  },
  methods: {
    ...mapActions("marketing/banner", [
      "getLiveBanners",
      "getScheduledBanners",
      "getDraftBanners",
      "getDeactivatedBanners",
      "updateBanner",
      "orderBanners",
    ]),
    format,
    redirectToEdit(bannerId) {
      this.$router.push(`/marketing/banners/${bannerId}`);
    },
    handleSelectBanner(banner, mode) {
      this.selectedBanner = banner;
      this.mode = mode;
      this.$bvModal.show("banner-modal");
    },
    async handleChangeStatusBanner() {
      try {
        const statuses = {
          active: 1,
          inactive: 2,
          draft: 3,
        };
        const result = await this.updateBanner({
          bannerId: this.selectedBanner.bannerId,
          data: {
            status:
              this.mode === "active"
                ? statuses.active
                : this.mode === "delete"
                ? statuses.inactive
                : statuses.draft,
          },
        });
        if (result)
          this.showToastSuccess(
            `${
              this.mode === "active" ? "Active" : "Delete"
            } banner successfully!`
          );
        this.$bvModal.hide("banner-modal");
        await Promise.all([
          this.getLiveBanners({ page: this.page.live }),
          this.getScheduledBanners({ page: this.page.scheduled }),
          this.getDeactivatedBanners({ page: this.page.deactivated }),
          this.getDraftBanners({ page: this.page.draft }),
        ]);
      } catch (error) {
        this.showToastError(
          `Failed to ${
            this.mode === "active" ? "active" : "delete"
          } banner. Please try again.`
        );
      }
    },
    async handleSaveOrder() {
      const orderedIds = this.liveBanners.map((banner, index) => ({
        bannerId: banner.bannerId,
        orderNumber: index + 1,
      }));
      const result = await this.orderBanners({ banners: orderedIds });
      if (result) {
        this.showToastSuccess("Banner order updated successfully!");
      }
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();
    return {
      showToastSuccess,
      showToastError,
    };
  },
};
</script>

<style scoped></style>
